'use client';

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/app/store';
import { 
  selectCurrentThemeMode, 
  selectCurrentColorTheme, 
  selectIsThemeInitialized 
} from '@/app/store/slices/themeSlice';
import { useAuth } from '../../../hooks/useAuth';
import { initializeSimpleTheme } from '../utils/initializeSimpleTheme';
import { updateHtmlAttributes } from '../utils/simpleThemeUtils';

/**
 * ThemeProvider component
 * Initializes theme preferences on app load
 * Handles both authenticated and guest users
 * Applies theme to document automatically
 */
const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch();
  const isThemeInitialized = useSelector(selectIsThemeInitialized);
  const currentMode = useSelector(selectCurrentThemeMode);
  const currentColorTheme = useSelector(selectCurrentColorTheme);
  const { isAuthenticated } = useAuth();
  
  // Use new auth system directly
  const { user } = useAuth();

  useEffect(() => {
    // Only initialize once
    if (isThemeInitialized) {
      return;
    }
    
    // Initialize theme based on authentication status
    initializeSimpleTheme(dispatch, user?.id ? parseInt(user.id) : undefined, isAuthenticated);
    
  }, [dispatch, isThemeInitialized, isAuthenticated, user?.id]);

  // Apply theme to document when theme changes
  useEffect(() => {
    if (isThemeInitialized) {
      updateHtmlAttributes(currentMode, currentColorTheme);
    }
  }, [currentMode, currentColorTheme, isThemeInitialized]);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (!isThemeInitialized || currentMode !== 'system') {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      updateHtmlAttributes(currentMode, currentColorTheme);
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [currentMode, currentColorTheme, isThemeInitialized]);

  return <>{children}</>;
};

export default ThemeProvider;